import { inject } from 'vue'
import { createRouter, createWebHistory } from "vue-router"
import { store } from '@/store/index.js'

import LayoutDefault from '@/layouts/Default'
import LayoutFullPage from '@/layouts/FullPage'

import AuthenticationByKeys from '@/views/AuthenticationByKeys'
import SelectEstablishment from '@/views/SelectEstablishment'
import SelectGroups from '@/views/SelectGroups'

import Care from '@/views/Care'
import Chat from '@/views/Chat'
import Dashboard from '@/views/Dashboard'
import Employees from '@/views/Employees'
import Logout from '@/views/Logout'
import Planning from '@/views/Planning'
import About from '@/views/About'

import Activities from '@/views/Activities'
import ActivitiesIndex from '@/views/activities/Index'
import ActivitiesForm from '@/views/activities/Form'

import Children from '@/views/Children'
import ChildrenGeneral from '@/views/children/General'
import ChildrenCarers from '@/views/children/Carers'
import ChildrenPlanning from '@/views/children/Planning'
import ChildrenActivities from '@/views/children/Activities'
import ChildrenPhotos from '@/views/children/Photos'
import ChildrenForms from '@/views/children/Forms'

import ChildrenDossier from '@/views/children/Dossier'
import ChildrenChildTrackingSystem from '@/views/children/ChildTrackingSystem'

import Photos from '@/views/Photos'
import PhotosBrowse from '@/views/photos/Browse'
import PhotosUpload from '@/views/photos/Upload'

import ChildTrackingSystem from '@/views/ChildTrackingSystem'
import ChildTrackingSystemAnalyse from '@/views/childtrackingsystem/Analyse'
import ChildTrackingSystemEditObservationList from '@/views/childtrackingsystem/EditObservationList'
import ChildTrackingSystemResults from '@/views/childtrackingsystem/Results'
import ChildTrackingSystemTriggers from '@/views/childtrackingsystem/Triggers'
import ChildTrackingSystemForms from '@/views/childtrackingsystem/Forms'
import ChildTrackingSystemGrafic from '@/views/childtrackingsystem/Grafic'
import ChildTrackingSystemParentContact from '@/views/childtrackingsystem/parentContact'
import ChildTrackingSystemGeneralForm from '@/views/childtrackingsystem/generalForm'
import ChildTrackingSystemObservationForm from '@/views/childtrackingsystem/ObservationForm'
import ChildTrackingSystemGroupOverview from '@/views/childtrackingsystem/GroupOverview'

import Gastouder from '@/views/Gastouder'
import GastouderHoursOpen from '@/views/gastouder/HoursOpen'
import GastouderHoursInsert from '@/views/gastouder/HoursInsert'
import GastouderHoursCheck from '@/views/gastouder/HoursCheck'
import GastouderHoursSubmitted from '@/views/gastouder/HoursSubmitted'
import GastouderHoursView from '@/views/gastouder/HoursView'

import Transfer from '@/views/Transfer'
import TransferMessages from "@/views/transfer/Messages"
import TransferNews from "@/views/transfer/News"
import TransferFilesPersonal from "@/views/transfer/FilesPersonal"
import TransferFilesGeneral from "@/views/transfer/FilesGeneral"
import TransferPersonRegister from "@/views/transfer/PersonRegister"
import TransferMutationRequests from "@/views/transfer/MutationRequests"

import TransferReports from '@/views/transfer/Reports'
import ReportsEmployeeWeekOverview from '@/views/reports/Employee-Week-Overview'
import ReportsChildPlanningMutations from '@/views/reports/Child-Planning-Mutations'

const getBaseUrl = () => {
  const path = window.location.pathname;
  if (path.startsWith('/groepsapp')) return '/groepsapp/';
  if (path.startsWith('/gastouderapp')) return '/gastouderapp/';
  return '/';
};

function guardUserIsLoggedIn (to, from, next) {
  if (store.state.session === null) {
    if (window.location.pathname.startsWith('/gastouderapp')) {
      window.location.href = process.env.VUE_APP_BASE_LOGIN + '/authentication/kidsadmin-sso/gastouderapp'
    } else {
      window.location.href = process.env.VUE_APP_BASE_LOGIN + '/authentication/kidsadmin-sso/groepsapp'
    }
  } else {
    next()
  }
}

function guardUserIsLoggedInAndEstablishmentIsSelected (to, from, next) {
  if (store.state.session === null) {
    if (window.location.pathname.startsWith('/gastouderapp')) {
      window.location.href = process.env.VUE_APP_BASE_LOGIN + '/authentication/kidsadmin-sso/gastouderapp'
    } else {
      window.location.href = process.env.VUE_APP_BASE_LOGIN + '/authentication/kidsadmin-sso/groepsapp'
    }
  } else if (store.state.selectedEstablishment === null) {
    next('/select-establishment')
  } else {
    next()
  }
}

function guardUserIsLoggedInAndEstablishmentAndGroupAreSelected (to, from, next) {
  if (store.state.session === null) {
    if (window.location.pathname.startsWith('/gastouderapp')) {
      window.location.href = process.env.VUE_APP_BASE_LOGIN + '/authentication/kidsadmin-sso/gastouderapp'
    } else {
      window.location.href = process.env.VUE_APP_BASE_LOGIN + '/authentication/kidsadmin-sso/groepsapp'
    }
  } else if (store.state.selectedEstablishment === null) {
    next('/select-establishment')
  } else if (store.state.selectedGroups === null) {
    next('/select-group')
  } else {
    next()
  }
}

export const router = createRouter({
  base: getBaseUrl(),
  history: createWebHistory(getBaseUrl()),
  routes: [
    {
      path: '/select-establishment',
      name: 'SelectEstablishment',
      beforeEnter: guardUserIsLoggedIn,
      component: SelectEstablishment
    },
    {
      path: '/select-group',
      name: 'SelectGroup',
      beforeEnter: guardUserIsLoggedInAndEstablishmentIsSelected,
      component: SelectGroups
    },
    {
      path: '/logout',
      component: Logout
    },
    {
      path: '/authentication/by-keys',
      component: LayoutFullPage,
      children: [
        {
          path: ':token1/:token2/:token3/:idClient',
          name: 'authentication-by-keys',
          component: AuthenticationByKeys,
        }
      ]
    },
    {
      path: '/dashboard',
      component: LayoutDefault,
      beforeEnter: guardUserIsLoggedInAndEstablishmentAndGroupAreSelected,
      children: [
        {
          path: '',
          name: 'Dashboard',
          component: Dashboard
        },
        {
          path: '/activities',
          component: Activities,
          props: true,
          children: [
            {
              name: 'Activities',
              path: '',
              component: ActivitiesIndex,
              props: true
            },
            {
              name: 'ActivitiesForm',
              path: 'form',
              component: ActivitiesForm,
              props: true
            },
            {
              name: 'ChildActivities',
              path: 'activities',
              component: ChildrenActivities,
              props: true
            },
            {
              name: 'ChildPhotos',
              path: 'photos',
              component: ChildrenPhotos,
              props: true
            }
          ]
        },
        {
          path: '/children',
          name: 'Children',
          component: Children
        },
        {
          path: '/children/:id',
          component: Children,
          props: true,
          children: [
            {
              name: 'ChildGeneral',
              path: '',
              component: ChildrenGeneral,
              props: true
            },
            {
              name: 'ChildCarers',
              path: 'carers',
              component: ChildrenCarers,
              props: true
            },
            {
              name: 'ChildPlanning',
              path: 'planning',
              component: ChildrenPlanning,
              props: true
            },
            {
              name: 'ChildActivities',
              path: 'activities',
              component: ChildrenActivities,
              props: true
            },
            {
              name: 'ChildPhotos',
              path: 'photos',
              component: ChildrenPhotos,
              props: true
            },
            {
              name: 'ChildrenForms',
              path: 'forms',
              component: ChildrenForms,
              props: true
            },
            {
              name: 'children-childtrackingsystem',
              path: 'childtrackingsystem',
              component: ChildrenChildTrackingSystem,
              props: true
            },
            {
              name: 'ChildDossier',
              path: 'dossier',
              component: ChildrenDossier,
              props: true
            }
          ]
        },
        {
          path: '/care',
          name: 'Care',
          component: Care
        },
        {
          path: '/chat',
          name: 'Chat',
          component: Chat
        },
        {
          path: '/employees',
          name: 'Employees',
          component: Employees
        },
        {
          path: '/planning',
          name: 'Planning',
          component: Planning
        },
        {
          path: '/childtrackingsystem',
          name: 'ChildTrackingSystem',
          component: ChildTrackingSystem
        },
        {
          path: '/childtrackingsystem',
          component: ChildTrackingSystem,
          props: true,
          children: [
            {
              name: 'childtrackingsystem-analyse',
              path: 'analyse',
              component: ChildTrackingSystemAnalyse,
              props: true
            },
            {
              name: 'childtrackingsystem-editchildobservationlist',
              path: 'editobservationlist/:idResult',
              component: ChildTrackingSystemEditObservationList,
              props: true
            },
            {
              name: 'childtrackingsystem-results',
              path: 'results',
              component: ChildTrackingSystemResults,
              props: true
            },
            {
              name: 'childtrackingsystem-triggers',
              path: 'triggers',
              component: ChildTrackingSystemTriggers,
              props: true
            },
            {
              name: 'childtrackingsystem-forms',
              path: 'forms',
              component: ChildTrackingSystemForms,
              props: true
            },
            {
              name: 'childtrackingsystem-observationform',
              path: 'editchildform/:idResult/:idChildForm',
              component: ChildTrackingSystemObservationForm,
              props: true
            },
            {
              name: 'childtrackingsystem-grafic',
              path: 'grafic',
              component: ChildTrackingSystemGrafic,
              props: true
            },
            {
              name: 'childtrackingsystem-parentcontact',
              path: 'parentContact',
              component: ChildTrackingSystemParentContact,
              props: true
            },
            {
              name: 'childtrackingsystem-generalform',
              path: 'generalForm/:idChild/:idForm/:idGeneralForm',
              component: ChildTrackingSystemGeneralForm,
              props: true
            },
            {
              name: 'childtrackingsystem-groupoverview',
              path: 'groupOverview',
              component: ChildTrackingSystemGroupOverview,
              props: true
            }
          ]
        },
        {
          path: '/gastouder',
          name: 'Gastouder',
          component: Gastouder,
        },
        {
          path: '/gastouder',
          component: Gastouder,
          props: true,
          children: [
            {
              name: 'GastouderHoursOpen',
              path: 'hours-open',
              component: GastouderHoursOpen,
              props: true
            },
            {
              name: 'GastouderHoursInsert',
              path: 'hours-open/insert/:idGastouderPeriod/:idChild/:date?',
              component: GastouderHoursInsert,
              props: true
            },
            {
              name: 'GastouderHoursCheck',
              path: 'hours-open/check/:idGastouderPeriod/:idChild',
              component: GastouderHoursCheck,
              props: true
            },
            {
              name: 'GastouderHoursSubmitted',
              path: 'hours-submitted',
              component: GastouderHoursSubmitted,
              props: true
            },
            {
              name: 'GastouderHoursView',
              path: 'hours-submitted/view/:idGastouderPeriod/:idChild',
              component: GastouderHoursView,
              props: true
            },
          ]
        },
        {
          path: '/photos',
          component: Photos,
          props: true,
          children: [
            {
              name: 'PhotosUpload',
              path: '',
              component: PhotosUpload,
              props: true
            },
            {
              name: 'PhotosBrowse',
              path: 'browse',
              component: PhotosBrowse,
              props: true
            }
          ]
        },
        {
          path: '/transfer',
          name: 'Transfer',
          component: Transfer
        },
        {
          path: '/transfer',
          component: Transfer,
          children: [
            {
              name: 'transfer-messages',
              path: 'messages',
              component: TransferMessages
            },
            {
              name: 'transfer-news',
              path: 'news',
              component: TransferNews
            },
            {
              name: 'transfer-files-personal',
              path: 'filesPersonal',
              component: TransferFilesPersonal
            },
            {
              name: 'transfer-files-general',
              path: 'filesGeneral',
              component: TransferFilesGeneral
            },
            {
              name: 'transfer-person-register',
              path: 'personRegister',
              component: TransferPersonRegister
            },
            {
              name: 'transfer-reports',
              path: 'Reports',
              component: TransferReports
            },
            {
              name: 'ReportsEmployeeWeekOverview',
              path: 'reports/employee_week_overview',
              component: ReportsEmployeeWeekOverview,
              props: true
            },
            {
              name: 'ReportsChildPlanningMutations',
              path: 'reports/child_planning_mutations',
              component: ReportsChildPlanningMutations
            },
            {
              name: 'transfer-mutation-requests',
              path: 'mutationRequests',
              component: TransferMutationRequests
            },
          ]
        },
        {
          path: '/about',
          name: 'About',
          component: About
        },
        {
          path: '/about',
          name: 'About',
          component: About
        }
      ]
    },
    {
      // 404
      path: '/:pathMatch(.*)*',
      redirect: '/dashboard'
    }
  ],
  scrollBehavior: function (to) {
    if (to.hash) {
      return {selector: to.hash}
    } else {
      return {x: 0, y: 0}
    }
  }
})

// periodically check if application settings need to be refreshed
router.beforeEach(async () => {
  const $store = inject('store')
  const $moment = inject('$moment')

  const path = window.location.pathname;
  if (path.startsWith('/gastouderapp')) {
    document.title = 'Gastouderapp';
  } else {
    document.title = 'Groepsapp';
  }

  if (
    $store.getters.session !== null && (
      $store.getters.settingsRefreshAt === null ||
      $moment($store.getters.settingsRefreshAt) < $moment()
    )
  ) {
    console.log('Re-loading application settings')
    $store.dispatch('loadApplicationSettings')
  }

  // periodically check if auth profile needs to be refreshed
  if (
    $store.getters.session !== null && (
      $store.getters.authProfileRefreshAt === null ||
      $moment($store.getters.authProfileRefreshAt) < $moment()
    )
  ) {
    console.warn($store.getters.authProfileRefreshAt, $moment($store.getters.authProfileRefreshAt))
    $store.dispatch('loadAuthProfile')
  }
});
